import { formatMoney } from '@theme/money-formatting';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * <uterific-preorder> — the pack tabs and the quantity stepper on the preorder panel.
 *
 * Every price the tabs switch between is rendered by Liquid's `money` filter up front and
 * parked on the input as a data attribute, so this file never formats currency itself — it
 * swaps already-formatted strings. That keeps multi-currency and every locale's money
 * format correct without duplicating Shopify's formatting rules in JavaScript.
 *
 * Changing pack also swaps which "your preorder includes" extras are shown, since a bigger
 * pack can carry different giveaways: each extra declares the packs it belongs to.
 *
 * The one place a price is computed rather than swapped is the total, which has to be the
 * pack price times the quantity. That multiplication cannot use a pre-formatted string, so
 * the raw minor units ride along on the input and the theme's own formatMoney — the same
 * rules as the `money` filter — turns the product back into currency.
 */

class UterificPreorder extends HTMLElement {
  connectedCallback() {
    this.packs = Array.from(this.querySelectorAll('[data-ut-pack]'));
    this.variantInput = this.querySelector('[data-ut-variant]');
    this.quantityInput = this.querySelector('[data-ut-quantity]');
    this.quantityLabel = this.querySelector('[data-ut-quantity-label]');
    this.extras = Array.from(this.querySelectorAll('[data-ut-extra]'));
    this.submit = this.querySelector('[data-ut-submit]');

    this.fields = {
      price: this.querySelector('[data-ut-price]'),
      compare: this.querySelector('[data-ut-compare]'),
      perPatch: this.querySelector('[data-ut-per-patch]'),
      perPatchWas: this.querySelector('[data-ut-per-patch-was]'),
      off: this.querySelector('[data-ut-off]'),
      total: this.querySelector('[data-ut-total]'),
      totalWas: this.querySelector('[data-ut-total-was]'),
    };

    this.min = 1;
    this.max = parseInt(this.getAttribute('max-quantity'), 10) || 9;
    this.quantity = this.min;
    this.moneyFormat = this.getAttribute('money-format') || '';
    // Card layout keeps each pack's extras inside its own card, so there is no shared list
    // whose height needs reserving — and no shared list to measure in the first place.
    this.cards = this.hasAttribute('cards');
    this.currency = this.getAttribute('currency') || 'USD';
    // What one unit includes. The property posted to the cart is this plus the quantity.

    this.packs.forEach((input) =>
      input.addEventListener('change', () => {
        this.applyPack();
        this.showPackSlide();
      })
    );

    this.querySelectorAll('[data-ut-step]').forEach((button) => {
      button.addEventListener('click', () => {
        this.setQuantity(this.quantity + Number(button.dataset.utStep));
      });
    });

    if (this.submit) {
      this.form = this.closest('form');
      if (this.form) {
        // The label confirms the add without navigating away from the panel.
        this.form.addEventListener('submit', (event) => this.onSubmit(event));
      }
    }

    this.applyPack();
    this.setQuantity(this.min);

    this.onResize = () => {
      if (!this.resizeFrame) {
        this.resizeFrame = requestAnimationFrame(() => {
          this.resizeFrame = 0;
          this.reserveExtrasHeight();
        });
      }
    };
    window.addEventListener('resize', this.onResize);
  }

  disconnectedCallback() {
    if (this.onResize) window.removeEventListener('resize', this.onResize);
    if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
  }

  applyPack() {
    // Liquid checks the first tab, so the fallback only matters if that ever stops being
    // true — better a priced panel than an empty one.
    const chosen = this.packs.find((input) => input.checked) || this.packs[0];
    if (!chosen) return;

    const set = (field, value) => {
      if (this.fields[field] && value !== undefined) this.fields[field].textContent = value;
    };

    set('price', chosen.dataset.price);
    set('compare', chosen.dataset.compare);
    set('perPatch', chosen.dataset.perPatch);
    set('perPatchWas', chosen.dataset.perPatchWas);
    set('off', chosen.dataset.off);

    // A pack with no compare-at price has nothing to strike through.
    const hasCompare = chosen.dataset.compare && chosen.dataset.compare.trim() !== '';
    if (this.fields.compare) this.fields.compare.hidden = !hasCompare;
    if (this.fields.perPatchWas) {
      this.fields.perPatchWas.hidden = !chosen.dataset.perPatchWas || chosen.dataset.perPatchWas.trim() === '';
    }
    // The chip is the saving against the struck total, which the gifts alone can create.
    if (this.fields.off) {
      const chip = this.fields.off.closest('[data-ut-off-chip]') || this.fields.off;
      chip.hidden = !chosen.dataset.off || chosen.dataset.off === '0';
    }

    if (this.variantInput && chosen.dataset.variant) {
      this.variantInput.value = chosen.dataset.variant;
    }

    const pack = chosen.dataset.pack;
    this.reserveExtrasHeight();
    this.extras.forEach((extra) => {
      const only = (extra.dataset.packs || '')
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean);
      // No packs listed means the extra ships with every pack.
      extra.hidden = only.length > 0 && !only.includes(pack);
    });

    this.chosen = chosen;
    this.updateTotals();
  }

  /**
   * A gallery image can belong to a pack (its "Show when these packs are chosen" setting),
   * so picking the 3 + 1 brings up the picture of the 3 + 1 offer. Only on a real change:
   * on load the first pack is chosen and the gallery already opens on its own first image.
   */
  showPackSlide() {
    const pack = this.chosen?.dataset.pack;
    const carousel = this.closest('section')?.querySelector('uterific-carousel');
    if (!pack || !carousel?.slides || typeof carousel.go !== 'function') return;
    const index = carousel.slides.findIndex((slide) =>
      (slide.dataset.utForPacks || '')
        .split(',')
        .map((p) => p.trim())
        .includes(pack)
    );
    if (index >= 0) carousel.go(index);
  }

  /**
   * The total is the pack price times the quantity. Before this existed the panel showed
   * the pack price whatever the stepper said, so three boxes read "Total $20" while $60
   * went to the cart.
   */
  updateTotals() {
    const chosen = this.chosen || this.packs.find((input) => input.checked) || this.packs[0];
    if (!chosen) return;

    const money = (cents) => {
      if (!this.moneyFormat) return null;
      const value = parseInt(cents, 10);
      if (!Number.isFinite(value)) return null;
      const total = value * this.quantity;
      /*
       * Liquid renders these prices with `money_without_trailing_zeros`, so a whole amount
       * reads "$20" and not "$20.00". Switching the placeholder the same way keeps the
       * multiplied total in the panel's own style instead of changing shape at quantity 2.
       * On a currency that already has no decimals the swap is a no-op.
       */
      const format =
        total % 100 === 0
          ? this.moneyFormat
              .replace(/{{\s*amount\s*}}/, '{{amount_no_decimals}}')
              .replace(/{{\s*amount_with_comma_separator\s*}}/, '{{amount_no_decimals_with_comma_separator}}')
          : this.moneyFormat;
      return formatMoney(total, format, this.currency);
    };

    const total = money(chosen.dataset.priceCents);
    if (this.fields.total) {
      // One unit still prefers Liquid's own string, so nothing changes for the common case.
      this.fields.total.textContent =
        this.quantity === 1 ? chosen.dataset.price || total || '' : total || chosen.dataset.price || '';
    }

    if (this.fields.totalWas) {
      const wasCents = chosen.dataset.totalWasCents;
      const was = wasCents ? money(wasCents) : null;
      this.fields.totalWas.textContent =
        this.quantity === 1 ? chosen.dataset.totalWas || '' : was || '';
      // The word beside the figure is markup, not script, so hiding the figure alone would
      // leave a stray "value" behind. Hide the pair.
      const wasWrap = this.fields.totalWas.closest('[data-ut-total-was-wrap]') || this.fields.totalWas;
      wasWrap.hidden = !this.fields.totalWas.textContent;
    }
  }

  /**
   * The packs carry different numbers of extras, so switching between them would change
   * the list's height and shove the total and the button up or down the page. The list
   * instead reserves the height of its tallest pack, measured with every row shown, and
   * re-measured on resize since rows wrap differently across widths.
   */
  reserveExtrasHeight() {
    if (this.cards) return;
    const list = this.extras[0] && this.extras[0].parentElement;
    if (!list || this.extras.length === 0) return;

    const packs = this.packs.map((input) => input.dataset.pack);
    const wasHidden = this.extras.map((extra) => extra.hidden);
    this.extras.forEach((extra) => {
      extra.hidden = false;
    });
    const heights = this.extras.map((extra) => extra.getBoundingClientRect().height);
    this.extras.forEach((extra, i) => {
      extra.hidden = wasHidden[i];
    });
    const gap = parseFloat(getComputedStyle(list).rowGap) || 0;

    let tallest = 0;
    packs.forEach((pack) => {
      let rows = 0;
      let height = 0;
      this.extras.forEach((extra, i) => {
        const only = (extra.dataset.packs || '')
          .split(',')
          .map((p) => p.trim())
          .filter(Boolean);
        if (only.length === 0 || only.includes(pack)) {
          rows += 1;
          height += heights[i];
        }
      });
      if (rows > 1) height += gap * (rows - 1);
      tallest = Math.max(tallest, height);
    });

    list.style.minHeight = tallest > 0 ? `${Math.ceil(tallest)}px` : '';
  }

  setQuantity(next) {
    this.quantity = Math.max(this.min, Math.min(this.max, next));
    if (this.quantityInput) this.quantityInput.value = this.quantity;
    if (this.quantityLabel) this.quantityLabel.textContent = this.quantity;

    this.querySelectorAll('[data-ut-step]').forEach((button) => {
      const step = Number(button.dataset.utStep);
      button.disabled =
        (step < 0 && this.quantity <= this.min) || (step > 0 && this.quantity >= this.max);
    });

    // The total follows the stepper, not just the pack.
    this.updateTotals();
  }

  /**
   * Add to the cart without leaving the page.
   *
   * A plain form post to /cart/add is answered with a redirect to /cart, which is how the
   * panel was throwing the reader out of the page they were reading to buy from. So the
   * submit is intercepted and the add goes over fetch instead.
   *
   * The work is dispatching Horizon's own CartLinesUpdateEvent rather than opening the
   * drawer directly. The drawer carries auto-open and listens for that event; so does the
   * cart icon's count, and so does the cart items component. Opening the drawer by hand
   * would move the panel and leave the count stale. One event keeps all three honest, and
   * it is the same event product-form.js dispatches, so this panel behaves like every
   * other add-to-cart in the theme.
   *
   * Falls through to the native post whenever the drawer is not there to receive it —
   * cart_type set to page, or the cart page itself, where a drawer would be absurd. The
   * markup stays a real form with a real action, so it also still works with no JS at all.
   */
  onSubmit(event) {
    const drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return;

    event.preventDefault();
    this.confirm();

    const body = new FormData(this.form);

    // Ask for the sections the cart renders itself from, so the drawer has fresh markup
    // to morph in rather than a stale copy of a cart from before this add.
    const sections = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    if (sections.length > 0) body.append('sections', sections.join(','));

    const quantity = Number(body.get('quantity')) || 1;
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(body.get('id')), quantity }],
        promise: deferred.promise,
      })
    );

    const cfg = fetchConfig('javascript', { body });

    fetch(Theme.routes.cart_add_url, { ...cfg, headers: { ...cfg.headers, Accept: 'text/html' } })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: { description: response.description, errors: response.errors },
            })
          );
          deferred.reject(new Error(response.message || 'Add to cart failed'));
          return;
        }

        // The add response reports what this call put in; the cart reports what is now
        // there, which is what the drawer and the count need. Fetched the way the theme's
        // own product form fetches it, headers and credentials included.
        const cart = await fetch(`${Theme.routes.cart_url}.json`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        }).then((r) => {
          if (!r.ok) throw new Error(`Failed to fetch cart: ${r.status}`);
          return r.json();
        });
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            itemCount: cart.item_count,
            sections: response.sections,
            source: 'uterific-preorder',
            didError: false,
          },
        });
      })
      .catch((error) => {
        // The add may well have landed before whatever failed here, so the page is sent to
        // the cart rather than left claiming a success it cannot confirm.
        console.error(error);
        deferred.reject(error);
        window.location.href = Theme.routes.cart_url;
      });
  }

  confirm() {
    if (!this.submit) return;
    const done = this.submit.dataset.confirmLabel;
    const idle = this.submit.dataset.idleLabel;
    if (!done) return;
    const target = this.submit.querySelector('[data-ut-submit-label]') || this.submit;
    target.textContent = done;
    window.setTimeout(() => {
      target.textContent = idle;
    }, 1800);
  }
}

if (!customElements.get('uterific-preorder')) {
  customElements.define('uterific-preorder', UterificPreorder);
}
